import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
  PLATFORM_ID,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal
} from '@angular/core';
import {fromEvent, Subscription} from 'rxjs';
import {connectInfiniteHits} from 'instantsearch.js/es/connectors';
import {AlgoliaService} from '@core/services/algolia.service';
import {ProductService} from '@core/services/product.service';
import {debounceTime, tap} from 'rxjs/operators';
import {isPlatformBrowser, isPlatformServer, NgTemplateOutlet} from '@angular/common';
import {RoutingService} from '@core/services/routing.service';
import {LoadingIconComponent} from '@layout/components/general/loading-icon/loading-icon.component';
import {
  InfiniteHitsConnectorParams,
  InfiniteHitsWidgetDescription
} from 'instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits';
import {SendEventForHits} from 'instantsearch.js/es/lib/utils';
import {AlgoliaWineProduct} from '@core/types/algolia-wine-product';
import {InstantSearchComponent} from '@core/instantsearch/components/instantsearch.component';
import {TypedBaseWidgetComponent} from '@core/instantsearch/types-based-widget';
import {AlgoliaProduct} from '@core/interfaces/algolia-product';

@Component({
  selector: 'app-search-page-results',
  templateUrl: './search-page-results.component.html',
  styleUrls: ['./search-page-results.component.scss'],
  imports: [LoadingIconComponent, NgTemplateOutlet]
})
export class SearchPageResultsComponent extends TypedBaseWidgetComponent<InfiniteHitsWidgetDescription<AlgoliaWineProduct>, InfiniteHitsConnectorParams<AlgoliaWineProduct>> implements OnDestroy {
  instantSearchInstance = inject(InstantSearchComponent);
  protected ngZone = inject(NgZone);
  private algoliaService = inject(AlgoliaService);
  private productService = inject(ProductService);
  private routingService = inject(RoutingService);
  private platformId = inject(PLATFORM_ID);

  scrollSub: Subscription | undefined;

  hitsTemplate = input.required<TemplateRef<{
    item: { wineProduct: AlgoliaWineProduct, raw: AlgoliaProduct },
    index: number,
    sendEvent: any
  }>>();
  renderingPage = signal<number>(0);
  renderingMore = computed(() => this.renderingPage() !== this.state()?.results?.page);
  hitMinWidthPx = input.required<number>();

  useDefaultSpacing = input<boolean>(true);

  readonly bottomElement = viewChild<ElementRef<HTMLDivElement>>('bottomElement');

  readonly sortBy = input<(a: any, b: any) => number>();

  readonly hitsChange = output<AlgoliaWineProduct[]>();

  hits = computed(() => {
    return this.formatHits(this.state()?.hits as any)
  });
  ready = computed(() => this.state()?.isFirstPage !== undefined);

  public override state: WritableSignal<{
    hits: AlgoliaWineProduct[];
    isLastPage: boolean;
    isFirstPage: boolean;
    showMore: () => void;
    showPrevious: () => void;
    results: any;
    sendEvent: SendEventForHits;
    bindEvent: () => string;
    currentPageHits: AlgoliaWineProduct[];
  } | undefined> = signal(undefined);

  constructor() {
    super('InfiniteHits');

    this.createWidget(connectInfiniteHits as any, {
      cache: this.getCache()
    });

    effect(() => {
      this.hitsChange.emit(this.formatHits(this.state()?.hits as any).map(d => d.wineProduct));
    });

    effect(() => {
      this.renderingPage.set(this.state()?.results?.page);
    });

    if (typeof window !== 'undefined') {
      this.ngZone.runOutsideAngular(() => {
        this.scrollSub = fromEvent(window, 'scroll').pipe(
          debounceTime(100),
          tap(d => this.scrollEvent())
        ).subscribe()
      })
    }

  }

  scrollEvent() {
    const bottomElementValue = this.bottomElement();
    if (!bottomElementValue) {
      return;
    }
    const bottomElement = bottomElementValue.nativeElement;

    if (isPlatformBrowser(this.platformId) && this.elementInView(bottomElement)) {
      this.triggerShowMore();
    }
  }

  protected formatHits(hitsRaw: AlgoliaProduct[] | undefined): {
    wineProduct: AlgoliaWineProduct,
    raw: AlgoliaProduct
  }[] {
    const filtered = hitsRaw ? this.productService.filterProducts(hitsRaw) : [];
    const hits = filtered?.map(hit => {
      return {
        wineProduct: this.algoliaService.hitToWineProduct(hit as any),
        raw: hit
      }
    });
    const sortBy = this.sortBy();
    if (sortBy) {
      hits.sort((a, b) => sortBy(a, b));
    }
    return hits;
  }

  elementInView(element: HTMLElement): boolean {
    const position = element.getBoundingClientRect();
    if (position.height === 0 && position.width === 0) {
      return false;
    }

    const triggerBeforeElementInView = 300;

    return (
      position.top >= 0 &&
      position.bottom <= (window.innerHeight || document.documentElement.clientHeight) + triggerBeforeElementInView
    );
  }

  getCache() {
    return {
      read: ({state}: any) => {
        const key = JSON.stringify(this.getStateWithoutPage(state));
        return this.algoliaService.hitsCache[key];
      },
      write: ({state, hits}: any) => {
        const key = JSON.stringify(this.getStateWithoutPage(state));
        this.algoliaService.hitsCache[key] = hits;
      }
    };
  }

  protected getStateWithoutPage(state: any) {
    const {page, ...rest} = state || {};
    return rest;
  }

  get isInStoryblokEditor() {
    return this.routingService.getQueryParams()?.['_storyblok_release'] !== undefined;
  }


  get page() {
    return Math.ceil(this.hits.length / this.batchSize);
  }

  triggerShowMore() {
    if (this.renderingMore() || this.state()?.isLastPage || isPlatformServer(this.platformId) || this.isInStoryblokEditor) {
      return;
    }
    this.renderingPage.set(this.state()?.results.page + 1);
    this.state()?.showMore();
  }

  override ngOnDestroy() {
    this.scrollSub?.unsubscribe();
  }

  get helper(): any {
    return this.instantSearchInstance?.instantSearchInstance?.helper;
  }

  get batchSize() {
    return this.helper?.state?.hitsPerPage;
  }
}
